import {
  Clipboard,
  LayoutDashboard,
  BadgeCheck,
  ShieldAlert,
  Wallet,
  FolderKanban,
  Users,
  ScrollText,
  Settings,
  Share2,
  KeyRound,
  BarChart3,
  CreditCard,
  Stethoscope,
  SlidersHorizontal,
  MessageSquareCode,
  Percent,
  Siren,
  LayoutTemplate,
  Trophy,
  TrendingUp,
  type LucideIcon,
  ShieldCheck,
  Scale,
  Mail,
  MailOpen,
  MailPlus,
} from 'lucide-react';

/**
 * Mirrors backend/src/common/enums/roles.enum.ts UserRole exactly. This portal is a new
 * client of the existing authorization model (ADM-03) — no new roles are invented here.
 * Keep in sync manually; the backend enum is the source of truth.
 */
export enum UserRole {
  USER = 'USER',
  CAREGIVER = 'CAREGIVER',
  SPECIALIST = 'SPECIALIST',
  LEAD_SPECIALIST = 'LEAD_SPECIALIST',
  ADMIN = 'ADMIN',
  SUPPORT = 'SUPPORT',
}

/** Roles allowed into the portal at all (ROADMAP.md §17.1). ADMIN, SUPPORT, and LEAD_SPECIALIST (scoped to Dashboard/Specialist Verification/Case Management) can log in; everyone else is rejected. */
export const PORTAL_ROLES: readonly UserRole[] = [
  UserRole.ADMIN,
  UserRole.SUPPORT,
  UserRole.LEAD_SPECIALIST,
];

export const isPortalRole = (role: string | undefined | null): role is UserRole =>
  !!role && (PORTAL_ROLES as string[]).includes(role);

/**
 * The one permission this portal itself gates on (ADM-24/25, ROADMAP.md §17.4). Not a full
 * mirror of backend/src/common/enums/roles.enum.ts's Permission enum — that catalog is
 * admin-editable and fetched live from GET /permissions (RolesPermissions.tsx) instead of
 * hand-duplicated here, precisely to avoid the drift ADM-26 found in the app package.
 */
export const MANAGE_ROLES_PERMISSION = 'MANAGE_ROLES';

export type PortalSectionId =
  | 'dashboard'
  | 'specialist-verification'
  | 'trust-safety'
  | 'payments-finance'
  | 'case-management'
  | 'user-management'
  | 'audit-log'
  | 'system-ops'
  | 'referral-operations'
  | 'roles-permissions'
  | 'feature-analytics'
  | 'service-cards'
  | 'specialties'
  | 'service-rules'
  | 'pool-score-config'
  | 'pool-rankings'
  | 'lynk-alert-ops'
  | 'service-card-templates'
  | 'operational-settings'
  | 'prompt-studio'
  | 'surface-controls'
  | 'moderation-queue'
  | 'plans'
  | 'legal-documents'
  | 'care-plans'
  | 'service-analytics'
  | 'mail'
  | 'mail-admin'
  | 'email-templates';

export type NavGroupId =
  | 'clinical-operations'
  | 'trust-safety'
  | 'finance'
  | 'people-access'
  | 'communication'
  | 'platform';

/** Left-nav group headers, in display order. Sections with no `group` (Dashboard) render ungrouped under Overview. */
export const NAV_GROUPS: readonly { id: NavGroupId; label: string; icon: LucideIcon }[] = [
  /* Each group carries its own icon because, collapsed, the sidebar shows nothing but the
     icon rail — a group borrowing its first child's icon would make the rail read as that
     one section rather than as the group. */
  /* "Clinical Ops", not "Clinical Operations": the sidebar gives a group label ~156px at
     250px pinned, and the full phrase is the one label in the nav that overruns it. */
  { id: 'clinical-operations', label: 'Clinical Ops', icon: Stethoscope },
  { id: 'trust-safety', label: 'Trust & Safety', icon: ShieldAlert },
  { id: 'finance', label: 'Finance', icon: Wallet },
  { id: 'people-access', label: 'People & Access', icon: Users },
  { id: 'communication', label: 'Communication', icon: Mail },
  { id: 'platform', label: 'Platform', icon: Settings },
];

export interface PortalSection {
  id: PortalSectionId;
  label: string;
  path: string;
  icon: LucideIcon;
  /** Which collapsible nav group this section renders under. Omit for ungrouped (Dashboard only). */
  group?: NavGroupId;
  roles: readonly UserRole[];
  /**
   * Fine-grained gate alongside `roles` (ADM-24). Client-side UX only — same as `roles`,
   * the backend's own guards remain the actual security boundary (see ProtectedRoute).
   */
  requiredPermission?: string;
  /** Backlog item that ships this section's screen; sections not yet built render a stub. */
  status: 'available' | 'planned';
}

/** Left-nav menu, roles, and backlog mapping — mirrors ROADMAP.md §17.1 exactly. */
export const PORTAL_SECTIONS: readonly PortalSection[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    path: '/',
    icon: LayoutDashboard,
    roles: [UserRole.ADMIN, UserRole.SUPPORT, UserRole.LEAD_SPECIALIST],
    status: 'available', // ADM-04 — shipped with initial portal shell
  },
  {
    id: 'specialist-verification',
    label: 'Specialist Verification',
    path: '/specialist-verification',
    icon: BadgeCheck,
    group: 'clinical-operations',
    roles: [UserRole.ADMIN, UserRole.SUPPORT, UserRole.LEAD_SPECIALIST],
    status: 'available', // ADM-05
  },
  {
    id: 'trust-safety',
    label: 'Trust & Safety',
    path: '/trust-safety',
    icon: ShieldAlert,
    group: 'trust-safety',
    roles: [UserRole.ADMIN, UserRole.SUPPORT],
    status: 'available', // ADM-06
  },
  {
    id: 'payments-finance',
    label: 'Payments & Finance',
    path: '/payments-finance',
    icon: Wallet,
    group: 'finance',
    roles: [UserRole.ADMIN],
    status: 'available', // ADM-07
  },
  {
    id: 'case-management',
    label: 'Case Management',
    path: '/case-management',
    icon: FolderKanban,
    group: 'clinical-operations',
    roles: [UserRole.ADMIN, UserRole.SUPPORT, UserRole.LEAD_SPECIALIST],
    status: 'available', // ADM-08 — shipped with case management screen
  },
  {
    id: 'user-management',
    label: 'User Management',
    path: '/user-management',
    icon: Users,
    group: 'people-access',
    roles: [UserRole.ADMIN],
    status: 'available', // ADM-09
  },
  {
    id: 'audit-log',
    label: 'Audit Log',
    path: '/audit-log',
    icon: ScrollText,
    group: 'platform',
    roles: [UserRole.ADMIN],
    status: 'available', // ADM-11 — backend gap closed, viewer shipped
  },
  {
    id: 'system-ops',
    label: 'System Ops',
    path: '/system-ops',
    icon: Settings,
    group: 'platform',
    roles: [UserRole.ADMIN],
    status: 'available', // ADM-12/ADM-15 — system ops + one-time grace-period migration
  },
  {
    id: 'referral-operations',
    label: 'Referral Operations',
    path: '/referral-operations',
    icon: Share2,
    group: 'finance',
    roles: [UserRole.ADMIN],
    status: 'available', // ADM-13 — shipped with REF-26 referral foundation
  },
  {
    id: 'roles-permissions',
    label: 'Roles & Permissions',
    path: '/roles-permissions',
    icon: KeyRound,
    group: 'people-access',
    roles: [UserRole.ADMIN],
    requiredPermission: MANAGE_ROLES_PERMISSION,
    status: 'available', // ADM-22/25 — §17.4 Wave 2
  },
  {
    id: 'feature-analytics',
    label: 'Analytics',
    path: '/analytics',
    icon: BarChart3,
    group: 'platform',
    roles: [UserRole.ADMIN],
    status: 'available',
  },
  {
    id: 'service-cards',
    label: 'Service Cards',
    path: '/service-cards',
    icon: CreditCard,
    group: 'clinical-operations',
    roles: [UserRole.ADMIN],
    status: 'available', // ADM ops backend PR #380 (Task 4)
  },
  {
    id: 'specialties',
    label: 'Specialties',
    path: '/specialties',
    icon: Stethoscope,
    group: 'clinical-operations',
    roles: [UserRole.ADMIN],
    status: 'available', // ADM ops backend PR #380 (Task 5)
  },
  {
    id: 'service-rules',
    label: 'Service Rules',
    path: '/service-rules',
    icon: SlidersHorizontal,
    group: 'clinical-operations',
    roles: [UserRole.ADMIN],
    status: 'available', // ADM ops backend PR #380 (Task 6)
  },
  {
    id: 'pool-score-config',
    label: 'Pool Score Config',
    path: '/pool-score-config',
    icon: Percent,
    group: 'clinical-operations',
    // Mirrors backend PoolScoreAdminController's @Roles(ADMIN, SUPPORT) exactly.
    roles: [UserRole.ADMIN, UserRole.SUPPORT],
    status: 'available', // docs/pool-ranking.md §5 gap #9
  },
  {
    id: 'prompt-studio',
    label: 'Prompt Studio',
    path: '/prompt-studio',
    icon: MessageSquareCode,
    group: 'platform',
    // Mirrors backend PromptsAdminController's @Roles(ADMIN) exactly — publishing
    // a prompt changes what every patient is told, immediately and without a deploy.
    roles: [UserRole.ADMIN],
    status: 'available',
  },
  {
    id: 'operational-settings',
    label: 'Operational Settings',
    path: '/operational-settings',
    icon: SlidersHorizontal,
    group: 'platform',
    // Mirrors backend OperationalSettingsAdminController's @Roles(ADMIN) exactly —
    // these values decide the patient-facing approval countdown and the AI spend.
    roles: [UserRole.ADMIN],
    status: 'available',
  },
  {
    id: 'pool-rankings',
    label: 'Pool Ranking',
    path: '/pool-rankings',
    icon: Trophy,
    group: 'clinical-operations',
    // Mirrors backend PoolScoreAdminController's @Roles(ADMIN, SUPPORT) exactly.
    roles: [UserRole.ADMIN, UserRole.SUPPORT],
    status: 'available', // GET /pool-score/admin/rankings
  },
  {
    id: 'lynk-alert-ops',
    label: 'Lynk Alert™ Ops',
    path: '/lynk-alert-ops',
    icon: Siren,
    group: 'platform',
    // Mirrors backend LynkAlertAdminController's @Roles(ADMIN, SUPPORT) exactly.
    roles: [UserRole.ADMIN, UserRole.SUPPORT],
    status: 'available', // docs/admin-portal.md §17.7 ADM-44
  },
  {
    id: 'service-card-templates',
    label: 'Service Card Templates',
    path: '/service-card-templates',
    icon: LayoutTemplate,
    group: 'clinical-operations',
    // Mirrors backend ServiceCardTemplatesController's @Roles(ADMIN) exactly.
    roles: [UserRole.ADMIN],
    status: 'available', // docs/admin-portal.md §17.7 ADM-51
  },
  {
    id: 'surface-controls',
    label: 'Surface Controls',
    path: '/surface-controls',
    icon: LayoutTemplate,
    group: 'platform',
    // Mirrors backend SurfacesAdminController's @Roles(ADMIN) exactly — hiding or
    // reordering a surface changes what every app user sees, without a deploy.
    roles: [UserRole.ADMIN],
    status: 'available', // docs/administration.md §24.2.6
  },
  {
    id: 'plans',
    // "LynkPulse" is load-bearing, not decoration. With this section called just
    // "Plans & Pricing" and the clinical one called just "Care Plans", an admin
    // looking for the Free/Plus/Guardian subscription tiers reasonably opened
    // "Care Plans" and found patient treatment plans instead. Both labels now name
    // their subject outright.
    label: 'LynkPulse Plans & Pricing',
    path: '/plans',
    icon: CreditCard,
    group: 'finance',
    // Mirrors backend PlansAdminController's @Roles(ADMIN) exactly. Deliberately
    // the narrowest gate in this registry: unlike every other admin toggle here,
    // an edit on this screen changes what customers are BILLED. SUPPORT is
    // excluded for that reason, even though it can work the moderation queue.
    roles: [UserRole.ADMIN],
    status: 'available', // docs/administration.md §24.4.4
  },
  {
    id: 'legal-documents',
    label: 'Legal Documents',
    path: '/legal',
    icon: Scale,
    group: 'platform',
    // Mirrors backend LegalCampaignController's @Roles(ADMIN) exactly, and for a
    // sharper reason than most: this screen can email every account at once, and
    // that cannot be recalled. SUPPORT works the moderation queue but must not
    // reach the whole user base.
    roles: [UserRole.ADMIN],
    status: 'available', // spec §6 (L1)
  },
  {
    id: 'care-plans',
    // "Patient" disambiguates these from the LynkPulse subscription tiers — see the
    // note on the 'plans' section above. These are the clinical recommendation plans
    // a specialist writes FOR a patient (lab orders, referrals, medications).
    label: 'Patient Care Plans',
    path: '/care-plans',
    icon: Clipboard,
    group: 'clinical-operations',
    // Mirrors CarePlansAdminController's @Roles(ADMIN, SUPPORT). SUPPORT is admitted
    // because there is no PHI in the response to gate: clinical content is stripped
    // server-side, so this shows a plan's shape and never its contents.
    roles: [UserRole.ADMIN, UserRole.SUPPORT],
    status: 'available', // docs/administration.md §24.5.5
  },
  {
    id: 'moderation-queue',
    label: 'Moderation Queue',
    path: '/moderation-queue',
    icon: ShieldCheck,
    group: 'trust-safety',
    // SUPPORT as well as ADMIN: queue work, not administration. Mirrors
    // ModerationQueueController's @Roles(ADMIN, SUPPORT) exactly.
    roles: [UserRole.ADMIN, UserRole.SUPPORT],
    status: 'available', // docs/administration.md §24.8
  },
  {
    id: 'service-analytics',
    label: 'Service Analytics',
    path: '/service-analytics',
    icon: TrendingUp,
    group: 'platform',
    // Mirrors backend AnalyticsAdminController's @Roles(ADMIN) exactly.
    roles: [UserRole.ADMIN],
    status: 'available', // docs/admin-portal.md §17.7 ADM-54
  },
  {
    id: 'mail',
    label: 'Mail',
    path: '/mail',
    icon: Mail,
    group: 'communication',
    // Mirrors the backend mail module's @Roles(ADMIN, SUPPORT). Deliberately no
    // requiredPermission: the backend's mail.use permission is not in the portal's
    // client-side permission list yet, and gating on it here would hide the section from
    // everyone. The server still scopes every query by mailbox membership.
    roles: [UserRole.ADMIN, UserRole.SUPPORT],
    status: 'available', // spec 008 M2 — read-only inbox
  },
  {
    id: 'mail-admin',
    label: 'Mail admin',
    path: '/mail/admin',
    icon: MailPlus,
    group: 'communication',
    // Mirrors the backend's /mail/admin/* @Roles(ADMIN) exactly — creating mailboxes and
    // changing who can read them is administration, not inbox use, so SUPPORT is excluded.
    roles: [UserRole.ADMIN],
    status: 'available', // spec 008 M4 — mailbox administration
  },
  {
    id: 'email-templates',
    label: 'Email Templates',
    path: '/email-templates',
    icon: MailOpen,
    group: 'communication',
    // Mirrors backend EmailTemplatesAdminController's @Roles(ADMIN) exactly — publishing
    // changes what every patient and specialist is emailed, immediately and without a deploy.
    roles: [UserRole.ADMIN],
    status: 'available',
  },
];

export const sectionsForRole = (
  role: UserRole,
  permissions: readonly string[] = [],
): PortalSection[] =>
  PORTAL_SECTIONS.filter(
    (section) =>
      (section.roles as UserRole[]).includes(role) &&
      (!section.requiredPermission || permissions.includes(section.requiredPermission)),
  );
